import { Button, FileUpload, Icon } from "@ab-inbev-maz/compass";
import React, { useState } from "react";
import { useAlertStore } from "../../stores/alertStore";
import { isValidFileType } from "@/utils/file";

interface FileUploaderProps {
  title: string;
  description?: string;
  acceptedFileTypes?: string;
  maxSizeMB?: number;
  buttonText?: string;
  onFileSelect?: (file: File | null) => void;
  onUpload?: (file: File) => void;
}

const FileUploader = ({
  acceptedFileTypes = ".xlsx,.xls,.csv",
  maxSizeMB = 10,
  buttonText = "Cargar Archivo",
  onFileSelect,
  onUpload,
  title,
  description,
}: FileUploaderProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  const { showAlert } = useAlertStore();

  const handleFileChange = (event?: React.ChangeEvent<HTMLInputElement>) => {
    if (!event?.target.files?.length) {
      setSelectedFile(null);
      if (onFileSelect) onFileSelect(null);
      return;
    }
    const file = event.target.files[0];
    if (file) {
      if (!isValidFileType(file, acceptedFileTypes)) {
        showAlert({
          text: 'Tipo de archivo no permitido',
          message: `Solo se aceptan: ${acceptedFileTypes}`,
          status: 'error'
        });
        if (event.target) {
          event.target.value = '';
        }
        return;
      }
      if (file.size > maxSizeBytes) {
        showAlert({
          text: 'Archivo demasiado grande',
          message: `El archivo excede el tamaño máximo de ${maxSizeMB}MB`,
          status: 'error'
        });
        if (event.target) {
          event.target.value = '';
        }
        return;
      }
      setSelectedFile(file);
      if (onFileSelect) onFileSelect(file);
    }
  };

  return (
    <div className="p-6 mx-auto">
      <FileUpload
        accept={acceptedFileTypes}
        help={`El sistema acepta solamente 1 archivo ${acceptedFileTypes} por carga. El peso máximo aceptado es ${maxSizeMB}MB por archivo.`}
        img={<img alt="upload file" className="h-10 w-10" src="data:image/svg+xml,%3csvg%20viewBox='0%200%2040%2040'%20fill='none'%20xmlns='http://www.w3.org/2000/svg'%3e%3cpath%20fill-rule='evenodd'%20clip-rule='evenodd'%20d='M0.501221%206.66927C0.501221%204.82832%201.9936%203.33594%203.83455%203.33594H15.8346C16.3292%203.33594%2016.7983%203.55564%2017.1149%203.93563L20.7818%208.33594H36.1679C38.0089%208.33594%2039.5012%209.8283%2039.5012%2011.6693V33.3359C39.5012%2035.1769%2038.0089%2036.6693%2036.1679%2036.6693H3.83455C1.99362%2036.6693%200.501221%2035.1769%200.501221%2033.3359V6.66927Z'%20fill='%23007A78'%20/%3e%3cpath%20d='M24%2021L20%2017L16%2021'%20stroke='%239CFFFD'%20stroke-width='3'%20stroke-linecap='round'%20stroke-linejoin='round'%20/%3e%3cpath%20d='M20%2017L20%2031'%20stroke='%239CFFFD'%20stroke-width='3'%20stroke-linecap='round'%20stroke-linejoin='round'%20/%3e%3c/svg%3e"/>}
        instructions={<><span className="flex h-6 w-6"><Icon name="file/file-upload" /></span><p className="text-sm font-semibold underline">Selecciona o arrastra aquí tu documento</p></>}
        label={title || "Subir archivo(s)"}
        maxSize={maxSizeBytes}
        maxSizeErrorMessage={`El tamaño del archivo excede el límite de ${maxSizeMB}MB`}
        multiple={false}
        onChange={handleFileChange}
        className="max-w-full! [&>:first-child]:bg-white"
      >
        {description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
      </FileUpload>

      {
        buttonText && (
          <div>
            <Button
              label={buttonText}
              variant="primary"
              disabled={!selectedFile}
              className="w-full max-w-xs mt-2.5"
              onClick={() => selectedFile && onUpload?.(selectedFile)}
            />
          </div>
        )
      }

    </div>
  );
};

export default FileUploader;
